import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../api";
import toast from 'react-hot-toast';
import MainLayout from "./MainLayout";
import TopNavBar from "./TopNavBar";
import { useAuth } from "../context/AuthContext";
import {
  FiSearch, FiX, FiDownload, FiRefreshCw, FiFilter,
  FiChevronLeft, FiChevronRight, FiEye, FiClock,
  FiUser, FiGlobe, FiDatabase, FiAlertCircle, FiCheckCircle,
  FiPlusCircle, FiEdit, FiTrash2
} from "react-icons/fi";
import { Tooltip } from "antd";
import "../styles/audittrail.css";

/* ─────────────────────────────────────────────
   ACTION icon + color helpers
───────────────────────────────────────────── */
const ACTION_META = {
  created: { icon: <FiPlusCircle />, color: "#22c55e", bg: "#dcfce7", label: "Created" },
  updated: { icon: <FiEdit />, color: "#3b82f6", bg: "#dbeafe", label: "Updated" },
  deleted: { icon: <FiTrash2 />, color: "#ef4444", bg: "#fee2e2", label: "Deleted" },
  default: { icon: <FiDatabase />, color: "#8b5cf6", bg: "#ede9fe", label: "Event" },
};

function actionMeta(action = "") {
  return ACTION_META[action.toLowerCase()] || ACTION_META.default;
}

/* ─────────────────────────────────────────────
   Format helpers
───────────────────────────────────────────── */
function formatDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const today = new Date();
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";

  return d.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
}

function formatTime(isoStr) {
  return new Date(isoStr).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}

function capitalize(str) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
}

/* ─────────────────────────────────────────────
   Event Detail Modal
───────────────────────────────────────────── */
function EventDetailModal({ date, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  useEffect(() => {
    setLoading(true);
    api.get(`/api/audit-trail/events?date=${date}`)
      .then((r) => setEvents(r.data.data || []))
      .catch(() => toast.error("Failed to load events"))
      .finally(() => setLoading(false));
  }, [date]);

  return (
    <div className="at-modal-backdrop" onClick={onClose}>
      <div className="at-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="at-modal-header">
          <div className="at-modal-title">
            <FiDatabase size={16} />
            <span>Audit Log — {formatDate(date)}</span>
          </div>
          <button className="at-modal-close" onClick={onClose}><FiX size={18} /></button>
        </div>

        {/* Body */}
        <div className="at-modal-body">
          {loading ? (
            <div className="at-spinner-wrap"><div className="at-spinner" /></div>
          ) : events.length === 0 ? (
            <div className="at-empty">No events for this date.</div>
          ) : (
            <div className="at-timeline">
              {events.map((ev) => {
                const meta = actionMeta(ev.action);
                return (
                  <div className="at-timeline-item" key={ev.id}>
                    <div className="at-tl-icon" style={{ background: meta.bg, color: meta.color }}>
                      {meta.icon}
                    </div>
                    <div className="at-tl-content">
                      <div className="at-tl-header">
                        <span className="at-tl-time">{formatTime(ev.created_at)}</span>
                        <span className="at-action-badge" style={{ background: meta.bg, color: meta.color }}>
                          {capitalize(ev.action)}
                        </span>
                      </div>
                      <div className="at-tl-title">
                        {capitalize(ev.entity)} {capitalize(ev.action)}
                      </div>
                      {ev.description && (
                        <div className="at-tl-desc">{ev.description}</div>
                      )}
                      <div className="at-tl-meta">
                        {ev.performed_by && (
                          <span className="at-meta-chip"><FiUser size={11} />{ev.performed_by}</span>
                        )}
                        {ev.ip_address && (
                          <span className="at-meta-chip"><FiGlobe size={11} />IP: {ev.ip_address}</span>
                        )}
                        {ev.entity_id && (
                          <span className="at-meta-chip"><FiDatabase size={11} />ID: {ev.entity_id}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Filter Panel
───────────────────────────────────────────── */
function FilterPanel({ filters, onChange, entityTypes, onClose, onApply, onReset }) {
  return (
    <div className="at-filter-panel">
      <div className="at-filter-header">
        <span><FiFilter size={14} /> Filters</span>
        <button className="at-filter-close" onClick={onClose}><FiX size={15} /></button>
      </div>

      <div className="at-filter-body">
        <div className="at-filter-group">
          <label>Entity Type</label>
          <select value={filters.entity} onChange={(e) => onChange("entity", e.target.value)}>
            <option value="">All Entities</option>
            {entityTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div className="at-filter-group">
          <label>Action</label>
          <select value={filters.action} onChange={(e) => onChange("action", e.target.value)}>
            <option value="">All Actions</option>
            <option value="created">Created</option>
            <option value="updated">Updated</option>
            <option value="deleted">Deleted</option>
          </select>
        </div>

        <div className="at-filter-group">
          <label>From Date</label>
          <input type="date" value={filters.from} onChange={(e) => onChange("from", e.target.value)} />
        </div>

        <div className="at-filter-group">
          <label>To Date</label>
          <input type="date" value={filters.to} onChange={(e) => onChange("to", e.target.value)} />
        </div>
      </div>

      <div className="at-filter-footer">
        <button className="at-btn-ghost" onClick={onReset}>Reset</button>
        <button className="at-btn-primary" onClick={onApply}>Apply Filters</button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main Page
───────────────────────────────────────────── */
export default function AuditTrailPage() {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Data state
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityTypes, setEntityTypes] = useState([]);
  const [apiError, setApiError] = useState("");
  const [seeding, setSeeding] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);

  // Search
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimer = useRef(null);

  // Filters
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({ entity: "", action: "", from: "", to: "" });
  const [appliedFilters, setAppliedFilters] = useState({ entity: "", action: "", from: "", to: "" });

  // Modal
  const [selectedDate, setSelectedDate] = useState(null);

  /* ── Debounce search ── */
  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 400);
  };

  /* ── Load entity types once ── */
  useEffect(() => {
    api.get("/api/audit-trail/entity-types")
      .then((r) => setEntityTypes(r.data.data || []))
      .catch(() => { });
  }, []);

  /* ── Load grouped logs ── */
  const loadLogs = useCallback(() => {
    setLoading(true);
    setApiError("");
    const params = new URLSearchParams({
      search: debouncedSearch,
      page,
      limit,
      ...(appliedFilters.entity && { entity: appliedFilters.entity }),
      ...(appliedFilters.from && { from: appliedFilters.from + "T00:00:00" }),
      ...(appliedFilters.to && { to: appliedFilters.to + "T23:59:59" }),
    });
    api.get(`/api/audit-trail?${params}`)
      .then((r) => {
        setGroups(r.data.data?.groups || []);
        setTotal(r.data.data?.total || 0);
      })
      .catch((e) => {
        const msg = e.response?.data?.message || e.message || "API error";
        setApiError(msg);
        toast.error("Failed to load audit trail: " + msg);
      })
      .finally(() => setLoading(false));
  }, [debouncedSearch, page, appliedFilters]);

  /* ── Seed test data ── */
  const handleSeedTest = async () => {
    setSeeding(true);
    try {
      const baseUrl = process.env.REACT_APP_BASEURL || "";
      const res = await fetch(`${baseUrl}/api/audit-trail/seed-test`);
      const json = await res.json();
      if (json.success) {
        toast.success(`✅ Inserted ${json.data?.length || 5} sample audit rows!`);
        loadLogs();
      } else {
        toast.error("Seed failed: " + json.message);
      }
    } catch (e) {
      toast.error("Seed request failed: " + e.message);
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => { loadLogs(); }, [loadLogs]);

  /* ── Export CSV ── */
  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        search: debouncedSearch,
        ...(appliedFilters.entity && { entity: appliedFilters.entity }),
        ...(appliedFilters.from && { from: appliedFilters.from + "T00:00:00" }),
        ...(appliedFilters.to && { to: appliedFilters.to + "T23:59:59" }),
      });
      const res = await api.get(`/api/audit-trail/export?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-trail-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  /* ── Filter handlers ── */
  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    setPage(1);
    setShowFilter(false);
  };
  const resetFilters = () => {
    const blank = { entity: "", action: "", from: "", to: "" };
    setFilters(blank);
    setAppliedFilters(blank);
    setPage(1);
    setShowFilter(false);
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const hasActiveFilters = Object.values(appliedFilters).some(Boolean);

  return (
    <MainLayout 
      sidebarOpen={sidebarOpen} 
      toggleSidebar={() => setSidebarOpen((p) => !p)}
      title="Audit Trail"
      subtitle="Complete history of all system actions and changes"
    >
      <div className="at-page">

        {/* ── Toolbar ── */}
        <div className="at-toolbar" style={{ justifyContent: 'space-between', display: 'flex' }}>
          
          <div className="at-toolbar-left" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div className="at-search-wrap" style={{ width: '250px', margin: 0 }}>
              <FiSearch size={15} className="at-search-icon" />
              <input
                className="at-search-input"
                placeholder="Search..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
              {search && (
                <button className="at-search-clear" onClick={() => handleSearchChange("")}>
                  <FiX size={13} />
                </button>
              )}
            </div>

            <div className="at-filter-wrap">
              <button
                className={`at-btn-filter ${hasActiveFilters ? "at-btn-filter--active" : ""}`}
                onClick={() => setShowFilter(!showFilter)}
              >
                <FiFilter size={14} />
                Filters
                {hasActiveFilters && <span className="at-filter-dot" />}
              </button>

              {showFilter && (
                <FilterPanel
                  filters={filters}
                  onChange={(key, val) => setFilters((p) => ({ ...p, [key]: val }))}
                  entityTypes={entityTypes}
                  onClose={() => setShowFilter(false)}
                  onApply={applyFilters}
                  onReset={resetFilters}
                />
              )}
            </div>
          </div>

          <div className="at-toolbar-right" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Tooltip title="Export CSV">
              <button className="at-btn-icon-ghost" style={{ width: '36px', height: '36px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={handleExport}>
                <FiDownload size={16} />
              </button>
            </Tooltip>
          </div>
        </div>

        {/* ── Content ── */}
        <div className="at-content">
          {loading ? (
            <div className="at-loading-wrap">
              <div className="at-spinner" />
              <span>Loading audit trail…</span>
            </div>
          ) : groups.length === 0 ? (
            <div className="at-empty-state">
              <FiAlertCircle size={40} />
              <h3>{apiError ? "API Error" : "No audit logs found"}</h3>
              {apiError ? (
                <p style={{ color: "#ef4444", fontSize: "0.8rem", maxWidth: 380, wordBreak: "break-word" }}>
                  {apiError}
                </p>
              ) : (
                <p>Restart the API server and do any action (create/update/delete) to generate logs.</p>
              )}
              <button
                className="at-btn-primary"
                style={{ marginTop: 16 }}
                onClick={handleSeedTest}
                disabled={seeding}
              >
                {seeding ? "Inserting…" : "🧪 Insert Sample Data"}
              </button>
            </div>
          ) : (
            <>
              {/* Stats bar */}
              <div className="at-stats-bar">
                <span className="at-total-label">
                  <FiCheckCircle size={13} />
                  {total} date{total !== 1 ? "s" : ""} with activity
                </span>
              </div>

              {/* Groups table */}
              <div className="at-table-card">
                <table className="at-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Summary</th>
                      <th>Events</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((group) => {
                      const summary = group.events
                        .slice(0, 4)
                        .map((e) => `${capitalize(e.entity)} ${capitalize(e.action)}`)
                        .join(", ");
                      const moreCount = group.events.length > 4 ? group.events.length - 4 : 0;

                      return (
                        <tr key={group.date} className="at-table-row">
                          <td className="at-td-date">
                            <div className="at-date-label">{formatDate(group.date)}</div>
                            <div className="at-date-raw">{group.date}</div>
                          </td>
                          <td className="at-td-summary">
                            <span className="at-summary-text">{summary}</span>
                            {moreCount > 0 && (
                              <span className="at-more-chip">+{moreCount} more</span>
                            )}
                          </td>
                          <td className="at-td-events">
                            <span className="at-event-badge">
                              {group.events.length} Event{group.events.length !== 1 ? "s" : ""}
                            </span>
                          </td>
                          <td className="at-td-action">
                            <button
                              className="at-view-btn"
                              title="View log details"
                              onClick={() => setSelectedDate(group.date)}
                            >
                              <FiEye size={15} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {total > 0 && (
                <div className="at-pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', padding: '10px 0', borderTop: '1px solid #e2e8f0', fontSize: '13px', color: '#64748b' }}>
                  <div className="at-page-info">
                    Showing {Math.min((page - 1) * limit + 1, total)}-{Math.min(page * limit, total)} of {total}
                  </div>
                  <div className="at-page-btns" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <span>Rows per page:</span>
                    <select 
                      className="at-page-select" 
                      value={limit} 
                      onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                      style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none' }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={30}>30</option>
                    </select>
                    <button
                      className="at-page-btn"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                      style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}
                    >
                      <FiChevronLeft size={15} />
                    </button>
                    <button
                      className="at-page-btn"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                      style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}
                    >
                      <FiChevronRight size={15} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Detail modal */}
      {selectedDate && (
        <EventDetailModal date={selectedDate} onClose={() => setSelectedDate(null)} />
      )}
    </MainLayout>
  );
}
